/* Same-origin request helper. The csrftoken cookie IS the token: Django compares
 * whatever we send against the cookie on that same request, so reading the
 * cookie at dispatch can never be stale. Tokens baked into HTML (hidden inputs,
 * hx-headers, ss_grug.csrftoken) are hints that go stale on cached pages and
 * after login; everything below overrides them. Nothing here fetches, caches,
 * or synchronizes tokens. See docs/adr/2026-09-csrf.md. */
(function (window, document) {
    'use strict';
    const context = () => window.ss_grug || {};
    const pageUser = () => { const id = context().user_id; return id == null ? '' : String(id); };
    const unsafe = method => !/^(GET|HEAD|OPTIONS|TRACE)$/i.test(method || 'GET');
    const sameOrigin = url => new URL(url, document.baseURI).origin === window.location.origin;
    // Last match wins, like Django's cookie parser, in case a stale duplicate cookie exists.
    const token = () => {
        const matches = document.cookie.match(/(?:^|;)\s*csrftoken=([^;]*)/g) || [];
        return matches.length ? matches[matches.length - 1].split('=')[1].trim() : '';
    };

    function stamp(headers, method) {
        const result = new Headers(headers || {});
        const space = context().space_slug;
        if (space && !result.has('X-Grug-Space')) result.set('X-Grug-Space', space);
        if (unsafe(method)) {
            const value = token();
            if (value) result.set('X-CSRFToken', value);
            if (pageUser()) result.set('X-Grug-User', pageUser());
        }
        return Object.fromEntries(result.entries());
    }

    // Django checks a POST body's csrfmiddlewaretoken before the header, so a
    // stale copy carried in the body must be replaced too.
    function bodyWithToken(body, headers) {
        const value = token();
        if (!value) return body;
        const type = new Headers(headers).get('Content-Type') || '';
        if (typeof body === 'string' && type.startsWith('application/x-www-form-urlencoded')) {
            const params = new URLSearchParams(body);
            if (!params.has('csrfmiddlewaretoken')) return body;
            params.set('csrfmiddlewaretoken', value);
            return params.toString();
        }
        if ((body instanceof URLSearchParams || body instanceof FormData) && body.has('csrfmiddlewaretoken')) {
            body.set('csrfmiddlewaretoken', value);
        }
        return body;
    }

    // The notice stays until the user dismisses it or reloads: a later
    // unrelated request succeeding does not mean the failed write was saved.
    const MESSAGES = {
        csrf_failed: 'Not saved. Your input is still here. Copy any unsaved work, then reload the page and try again.',
        session_changed: 'Your sign-in session changed in another tab. Copy any unsaved work, then reload the page.',
    };
    function notice(text) {
        let status = document.getElementById('csrf-write-status');
        if (!status) {
            status = document.createElement('div');
            status.id = 'csrf-write-status';
            status.className = 'fixed bottom-4 left-4 right-4 z-50 rounded bg-gray-800 p-3 text-white';
            status.setAttribute('role', 'status');
            document.body.appendChild(status);
        }
        status.textContent = text + ' ';
        for (const [label, action] of [['Reload page', () => window.location.reload()], ['Dismiss', () => status.remove()]]) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'underline mr-3';
            button.textContent = label;
            button.addEventListener('click', action);
            status.appendChild(button);
        }
    }
    let recovering = false;
    function inspect(status, code, recoveryUrl) {
        if (status === 403 && code === 'space_not_member' && !recovering) {
            // The page's space is no longer available to this account.
            recovering = true;
            window.location.assign(/^\/(?!\/)/.test(recoveryUrl || '') ? recoveryUrl : '/');
        } else if ((status === 403 && code === 'csrf_failed') || (status === 409 && code === 'session_changed')) {
            notice(MESSAGES[code]);
        }
    }

    async function grugFetch(url, options = {}) {
        if (!sameOrigin(url)) return window.fetch(url, options);
        const headers = stamp(options.headers, options.method);
        const write = unsafe(options.method);
        const response = await window.fetch(url, {
            ...options, headers, body: write ? bodyWithToken(options.body, headers) : options.body,
        });
        // A failed error report never gets a notice of its own.
        if (new URL(url, document.baseURI).pathname !== '/-/error/') {
            inspect(response.status, response.headers.get('X-Grug-Error-Code'), response.headers.get('X-Grug-Recovery-Url'));
        }
        return response;
    }

    document.addEventListener('htmx:configRequest', event => {
        const config = event.detail;
        if (!sameOrigin(config.path)) return;
        config.headers = stamp(config.headers, config.verb);
        // POST only: htmx puts DELETE parameters in the query string, and a
        // session token must never appear in a URL.
        const value = token();
        if (value && /^post$/i.test(config.verb)) config.parameters.csrfmiddlewaretoken = value;
    }, true);
    document.addEventListener('htmx:responseError', event => {
        const xhr = event.detail.xhr;
        if (xhr?.getResponseHeader) inspect(xhr.status, xhr.getResponseHeader('X-Grug-Error-Code'), xhr.getResponseHeader('X-Grug-Recovery-Url'));
    }, true);

    // Native forms: refresh (or add) the hidden token at submit time and carry
    // the page's account so the server can refuse a write meant for another
    // session. No preventDefault, so every other submit handler runs exactly
    // as the browser specifies. Named controls can shadow form.action and
    // form.method (onboarding uses name="action"), so read markup attributes.
    document.addEventListener('submit', event => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        const submitter = event.submitter;
        const attr = name => submitter?.hasAttribute('form' + name) ? submitter.getAttribute('form' + name) : form.getAttribute(name);
        if ((attr('method') || 'get').toLowerCase() !== 'post' || !sameOrigin(attr('action') || window.location.href)) return;
        const set = (name, value) => {
            const inputs = Array.from(form.elements).filter(control => control.name === name);
            if (!inputs.length) {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = name;
                form.appendChild(input);
                inputs.push(input);
            }
            inputs.forEach(input => { input.value = value; });
        };
        if (token()) set('csrfmiddlewaretoken', token());
        if (pageUser()) set('grug_page_user', pageUser());
    }, true);

    // Per-user browser state. When the signed-in account changes (including a
    // sign-out that lands on the public page), drop every other account's
    // audio store, the recent searches and this browser's push subscription. Running on the
    // next page load means a logout is never delayed and a force-closed tab
    // is still cleaned up later.
    // Dead push endpoints are pruned server-side when a send returns 404/410.
    // The local-first lab (/-/labs/local-today/) keeps a copy of the notes in
    // the browser, per account: stores named grug-local-<user>-<space>,
    // localStorage keys lt-<name>-<user>, a cookie, and its service worker's
    // stored page. Only the page user's own stores and keys stay; with nobody
    // signed in, none of it does.
    try {
        const previous = window.localStorage.getItem('grug_last_user') || '';
        if (previous !== pageUser()) {
            window.localStorage.setItem('grug_last_user', pageUser());
            if (previous) {
                const user = pageUser();
                const labStore = name => name.startsWith('grug-local-') && !(user && name.startsWith('grug-local-' + user + '-'));
                // The signed-in account's own recordings stay: its store is
                // named as grug_audio.js names it (getIndexedDbName; an
                // over-long name carries a hash and still goes). Deleting it
                // lost them, and a delete that a tab of the same account held
                // up left its recorder with no store (2026-10-16).
                const scope = String(context().space_slug || context().user_username || '').trim().toLowerCase().replace(/[^a-z0-9_-]/g, '-');
                const ownAudioStore = user && scope ? 'audioStorage_' + scope : '';
                const audioStore = name => name.startsWith('audioStorage_') && name !== ownAudioStore;
                Object.keys(window.localStorage).forEach(key => {
                    const lab = key.startsWith('lt-') && !(user && key.endsWith('-' + user));
                    if (lab || key.startsWith('grug_recent_searches:')) window.localStorage.removeItem(key);
                });
                window.indexedDB?.databases?.().then(databases => databases.forEach(database => {
                    const name = database.name || '';
                    if (audioStore(name) || labStore(name)) window.indexedDB.deleteDatabase(name);
                })).catch(() => {});
                navigator.serviceWorker?.getRegistrations?.().then(registrations => registrations.forEach(registration => {
                    registration.pushManager?.getSubscription().then(subscription => subscription?.unsubscribe()).catch(() => {});
                    if (registration.scope?.endsWith('/-/labs/local-today/')) registration.unregister().catch(() => {});
                })).catch(() => {});
                // The lab's alone, and last: a browser that refuses one of
                // these never keeps the cleanup above from running. The
                // cookie is kept for the page's own address: the lab's, and
                // the site's where the page answers the app's addresses.
                document.cookie = 'lt_copy=; path=/-/labs/local-today/; max-age=0; samesite=lax';
                document.cookie = 'lt_copy=; path=/; max-age=0; samesite=lax';
                try {
                    window.caches?.keys().then(names => Promise.all(
                        names.filter(name => name.startsWith('local-today-')).map(name => window.caches.delete(name)),
                    )).catch(() => {});
                } catch (_) { /* no Cache Storage here */ }
            }
        }
    } catch (_) { /* storage unavailable: nothing to clean */ }

    window.grugFetch = grugFetch;
    window.grugHeaders = stamp; // For the one XMLHttpRequest upload that needs progress events.
})(window, document);
